import React, {useState, useEffect} from 'react';
import {CircularProgress, TextField, useTheme,  useMediaQuery, SwipeableDrawer, Drawer, Paper, ListItemButton, ListItemText, Link, Button, Box,  Divider, Grid2 as Grid, Typography, List, TableContainer, Table, TableHead,TableCell, TableRow, TableBody, ListSubheader} from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import { useErrorBoundary } from 'react-error-boundary';
import SegregatedFundsReportContent from '../../../types/reports/segregatedFundsReportContent';
import SearchIcon from '@mui/icons-material/Search';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';


const SegregatedFunds = (): JSX.Element => {

    document.title = 'OCPF - Segregated Account Funds';

    const [segregatedFundsReports, setSegregatedFundsReports] = useState<SegregatedFundsReportContent[]>([]);
    const [segregatedFundsReportsToDisplay, setSegregatedFundsReportsToDisplay] = useState<SegregatedFundsReportContent[]>([]);
    const { showBoundary } = useErrorBoundary(); 
    const [selectedFiler, setSelectedFiler] = useState<number>(0);
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isLoading, setIsLoading] = useState(true);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.target.value != '') {
    setSegregatedFundsReportsToDisplay(segregatedFundsReports.filter((filer) =>filer.cpfId.toString().includes(event.target.value.toString()) || filer.fundName.toLowerCase().includes(event.target.value.toLowerCase())));
    } else {
      setSegregatedFundsReportsToDisplay(segregatedFundsReports);
      setSelectedFiler(segregatedFundsReports[0].cpfId);
    }
      
    };
    

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

    useEffect(() => {
      getSegregatedFundsReports();
    }, []);
    
    const getSegregatedFundsReports = async () => {
  
      try{
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/reports/segregatedfunds`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get segregated account fund reports.'));
          }
          return result.json();
          
        }).then(result => {
          let sorted = result.sort((a: SegregatedFundsReportContent,b: SegregatedFundsReportContent) => a.cpfId - b.cpfId);
          setSegregatedFundsReports(sorted); 
          setSegregatedFundsReportsToDisplay(sorted);
          setSelectedFiler(sorted[0].cpfId);  
          setIsLoading(false);
        });
      }catch(error){
          console.error('Uncaught Error:', error);
      }
    }

    const drawerContent = (
      <List>
        <ListSubheader>Select a Segregated Fund</ListSubheader>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', marginBottom:2, width: '90%' }}>
          <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
          <TextField onChange={handleSearchChange} label="Search" variant="standard" />
        </Box>
        {segregatedFundsReportsToDisplay.map((filer, index) => (
          <ListItemButton  
            sx={{backgroundColor: filer.cpfId === selectedFiler ? 'rgba(25, 118, 210, 0.08)' : 'transparent'}} 
            key={index} 
            onClick={() => {
              renderSegregatedFundsReports(filer.cpfId);
              if (isMobile) {
                handleDrawerToggle();
              }
            }}
          >
            <ListItemText primary={filer.cpfId + ' ' + filer.fundName} />
          </ListItemButton>
        ))}
      </List>
    );

    const renderSegregatedFundsReports = (cpfid?: number) => {
      if (cpfid) setSelectedFiler(cpfid);
      return (
        <>
          {segregatedFundsReports.filter(filer => filer.cpfId === selectedFiler).map((filer, index) => ( 
            <Box component={Paper} sx={{ flexGrow: 1, p: 3, marginTop:2}} key={index}>
              <Grid container>
                <Grid size={10}>
                  <Typography variant='h6' component='span'>{filer.cpfId + ' ' + filer.fundName}<Typography variant='subtitle1'><i>{'Total Raised: '+ filer.receiptTotal}</i></Typography></Typography>
                </Grid>
                <Grid sx={{ display: 'flex', justifyContent: 'flex-end'}}  size={2}>
                  <Button href={'/filers?q=' + filer.cpfId} target='_blank'>Filer's Page</Button>
                </Grid>
              </Grid>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align='left'>Period</TableCell>
                      <TableCell align='right'>Receipts</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filer.segregatedFundReports.map((report, index) => (
                      <TableRow key={index}>
                        <TableCell align='left'>{report.reportingPeriod}</TableCell>
                        <TableCell align='right'><Link target="_blank" href={"/reports/displayreport?id=" + report.reportId}>{report.receiptTotal}</Link></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}
        </>
      )
    }

    return (
      <Main>
        <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
            variant="h5"
            align={'left'}
            data-aos={'fade-up'}
            gutterBottom
            sx={{
                fontWeight: 700,
            }}
            paddingTop={3}
            marginBottom={2}
            >
            Segregated Account Funds Reports
           
        </Typography>
        <Divider/>
        {isMobile && (
            <Button
              onClick={handleDrawerToggle}
              sx={{ ml: 2 }}
            >
              Select a Segregated Fund
            </Button>
          )}
        <Grid container spacing={2}>
          {!isMobile ? (
            <Grid size={2.5}>
              <Drawer
                variant="permanent"
                anchor="bottom"
                PaperProps={{
                  sx: {
                    position: 'static',
                    width: '100%',
                    height: '70vh',
                    boxShadow: 'none',
                    overflowY: 'auto',
                    '& .MuiDrawer-paper': {
                      position: 'static',
                    }
                  },
                }}
              >
                {drawerContent}
              </Drawer>
            </Grid>
          ) : (
            <SwipeableDrawer
              variant="temporary"
              anchor="bottom"
              open={mobileOpen}
              onClose={handleDrawerToggle}
              onOpen={handleDrawerToggle}
              ModalProps={{
                keepMounted: true, // Better mobile performance
              }}
              sx={{
                '& .MuiDrawer-paper': {
                  width: '100%',
                  height: '100%',
                  boxSizing: 'border-box',
                },
              }}
            >
              {drawerContent}
            </SwipeableDrawer>
          )}
          <Grid size={isMobile ? 12 : 9.5}>
            {renderSegregatedFundsReports()}
          </Grid>
        </Grid>

         {/*loading indicator*/}
               <CircularProgress
                    aria-label='Loading'
                    size={50}
                    className={isLoading? "": "hidden"}
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "20%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2
                     }}
                />

       
        
    </Container>
    </Main>
  );
};

export default SegregatedFunds;
